import React, { useState, useEffect } from 'react';
import { Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';
import { iRootState, iRootDispatch } from './store';
import EstateForm from './pages/estates-form/EstateForm';
import PageNotFound from './pages/PageNotFound';
import Estates from './pages/estates-list/Estates';
import EstatePreregisterListPage from './pages/estates-preregister-list';
import BlacklistPage from './pages/blacklist';
import Events from './pages/events/Events';
import ClaimEstates from './pages/claim_estates';
import Proposals from './pages/fire-insurance-proposal-list/Proposals';
import Policies from './pages/fire-insurance-policies-list/Policies';
import FireInsuranceBudgetForm from './pages/fire-insurance-budget-form/FireInsuranceBudgetForm';
import FireInsuranceBudget from './pages/fire-insurance/budget';
import FireInsuranceBudgetList from './pages/fire-insurance/budget-list';
import FireInsuranceCanceledList from './pages/fire-insurance/canceled-list';
import FireInsurancePolicyList from './pages/fire-insurance/policy-list';
import FireFireInsuranceInvoices from './pages/fire-insurance/invoices';
import FireInsuranceBatchesDashboard from './pages/fire-insurance/batches';
import FireInsuranceBatchDetails from './pages/fire-insurance/batches/Details';
import UsersForm from './pages/users-form/UsersForm';
import UsersList from './pages/users-list/UsersList';
import UsersEdit from './pages/users-edit/index';
import BondInsuranceForm from './pages/bond-insurance-budget-form/BondInsuranceForm';
import BondInsurancesForm from './pages/bond-insurance-list/BondInsurancesForm';
import CanceledBondInsurancesForm from './pages/canceled-bond-insurance-list/CanceledBondInsurancesForm';
import ExpiredBondInsurancesForm from './pages/bond-insurance-expired-list/ExpiredBondInsurancesForm';
import ReportForm from './pages/reports-form/ReportForm';
import FindRecord from './pages/find-record';
import {
  getBroker,
  getRoles,
  getFranchises,
  getMasterFranchises,
  getBrokerObjectFromGetBrokerDTO
} from './services/broker';
import { list as listEstates } from './services/estate';
import * as insurerService from './services/insurer';
import {
  BrokersToCustomDropDownOptions,
  EstatesToCustomDropDownOptions,
  InsurersToCustomDropDownOptions,
  RolesToCustomDropDownOptions,
  FranchisesToCustomDropDownOptions,
  MultFranchisesToCustomDropDownOptions
} from './util';
import { BrokerState } from './types/BrokerState';
import UpdateEstateForm from './pages/estates-edit-form/UpdateEstateForm';
import ChangeEmailForm from './pages/change-email/ChangeEmailForm';
import EstateUnitsListPage from './pages/estate-units';
import EstateUnitFormPage from './pages/estate-units/EstateUnitFormPage';

import {
  AvailableBrokersAndEstatesState,
  CurrentPageState,
  ScrollState,
  ToggledMenuState
} from './store/types/temp-types';
import { BrokerForGetBrokerResponseDTO } from './dtos/broker/broker-for-get-broker-response.dto';
import BondInsuranceProposalsForm from './pages/bond-insurance-proposal-list/BondInsuranceProposalsForm';
import BondInsurancePoliciesForm from './pages/bond-insurance-policy-list/BondInsurancePolicyForm';
import CapitalizationIndex from './pages/capitalization';
import PowerBIPage from './pages/powerbi';
import OperationalBIPage from './pages/operational-bi';
import Loader from './components/Loader';
import FranchisesList from './pages/franchises-list';
import FranchiseForm from './pages/franchise-form';
import { hasPermission } from './components/AuthorizationContainer';
import { ActionEnum } from './enums/authz-action.enum';
import { FeatureEnum } from './enums/authz-feature.enum';
import estatePreregisterForm from './pages/estate-preregister-form';
import Dashboard from './pages/dashboard';
import { connect } from 'react-redux';
import Budgets from './pages/fire-insurance-budget-list/Budgets';
import Sidebar from './components/Sidebar';
import { menuOptions } from './services/menu';
import BannerManagement from './pages/banner-management';
import {
  getFranchiseByLoggedUser,
  getMasterFranchiseByLoggedUser
} from './services/franchises';
import ClaimEstatesResponsibleTransfer from './pages/claims_estates_responsible_transfer/index';
import * as storage from './services/storage';
import { UserRolesEnum } from './enums/user-roles.enum';
import { MenuItemProps, SubMenuItemProps } from './containers/types';
import Reports from './pages/reports';
import masterFranchisesList from './pages/master-franchises-list';
import Notifications from './pages/notifications';
import FranchisesDashboard from './pages/franchises-dashboard';
import Financial from './pages/financial';
import IntegrationsDashboard from './pages/integrations-dashboard';
import LenderInsuranceList from './pages/lender-insurance';
import LenderInsuranceBatchList from './pages/lender-insurance-batches';
import StandaloneChargesPage from './pages/standalone-charges';
import PropertiesWalletPage from './pages/properties-wallet';
import PropertiesImportPage from './pages/properties-import';

type MatchParams = {
  name: string;
};

type MatchProps = RouteComponentProps<MatchParams> & {
  match: any;
};

const mapState = (state: iRootState) => ({
  user: state.user,
  toggledMenu: state.toggledMenu,
  currentPage: state.currentPage
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateScroll: (scroll: ScrollState) => dispatch.scroll.updateScroll(scroll),
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(
      availableBrokers
    ),
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

export type RoutesProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
  RouteComponentProps &
  ToggledMenuState;

const Routes = (componentProps: RoutesProps) => {
  const { currentPage, updateCurrentPage, user } = componentProps;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  async function getInsurers() {
    const cachedInsurers = storage.get('insurers:both');

    if (cachedInsurers) {
      const { data, timestamp } = JSON.parse(cachedInsurers);

      if (timestamp + 600000 > Date.now()) {
        return data;
      } else {
        storage.remove('insurers:both');
      }
    }

    const rawInsurers = await insurerService.list({ availability: 'BOTH' });
    const insurers = rawInsurers.filter(insurer => {
      return insurer;
    });

    storage.set(
      'insurers:both',
      JSON.stringify({ data: insurers, timestamp: Date.now() })
    );

    return insurers;
  }

  async function getAvailableBrokersAndEstates() {
    const insurers = await getInsurers();
    const bondInsurers = insurers.filter((insurer: any) =>
      insurer.availability.includes('BOND_INSURANCE')
    );

    const brokerDTO: BrokerForGetBrokerResponseDTO = await getBroker(
      '7c98d9d9-a975-45a3-9131-cc1961250182'
    );

    const broker: BrokerState | null = brokerDTO
      ? getBrokerObjectFromGetBrokerDTO(brokerDTO)
      : null;

    const enableFireInsuranceMenu = true;
    const accessibleEstates = user.accessibleEstates || [];
    const activeEstateId =
      user.activeEstateId || accessibleEstates?.[0]?.id || user.estate?.id || null;
    const brokerEstatesById = new Map(
      (broker?.estates || []).map((estate: any) => [estate.id, estate])
    );
    const isFranchiseUser = Boolean(user.franchiseId || user.multiFranchiseId);
    let estateOptions;
    if (accessibleEstates.length > 0) {
      estateOptions = accessibleEstates.map((estate: any, index: number) => {
        // Enrich accessible estates with broker estate payload to guarantee city/state.
        const fullEstate = brokerEstatesById.get(estate.id) || {};
        const estateCity =
          estate.estateCity || estate.city || fullEstate.estateCity || fullEstate.city || '';
        const estateState =
          estate.estateState ||
          estate.state ||
          estate.uf ||
          fullEstate.estateState ||
          fullEstate.state ||
          fullEstate.uf ||
          '';

        return {
          value: {
            ...fullEstate,
            ...estate,
            id: estate.id,
            name: estate.name,
            estateCity,
            estateState,
            standaloneChargesEnabled:
              estate.standaloneChargesEnabled || fullEstate.standaloneChargesEnabled || false
          },
          key: index,
          text: estate.name
        };
      });
    } else if (isFranchiseUser) {
      const franchiseEstates = await listEstates();
      estateOptions = EstatesToCustomDropDownOptions(true, franchiseEstates || []);
    } else {
      estateOptions = EstatesToCustomDropDownOptions(true, broker?.estates || []);
    }
    const roles = await getRoles();

    let franchises = [];
    let multiFranchises = [];

    const hasFranchisesPermission = hasPermission(
      ActionEnum.read,
      FeatureEnum.franchises
    );

    if (hasFranchisesPermission) {
      franchises = await getFranchises();
      multiFranchises = await getMasterFranchises();
    }

    if (user.franchiseId) {
      const franchiseUser = await getFranchiseByLoggedUser();
      franchises = [franchiseUser];
    }

    if (user.multiFranchiseId) {
      multiFranchises = [user.multiFranchise];
      franchises = user.multiFranchise?.franchises || [];
    }

    componentProps.updateAvailableBrokersAndEstates({
      brokers: BrokersToCustomDropDownOptions(true, broker ? [broker] : []),
      estates: estateOptions,
      insurers: InsurersToCustomDropDownOptions(true, insurers || []),
      roles: RolesToCustomDropDownOptions(roles || []),
      franchises: FranchisesToCustomDropDownOptions(franchises),
      multiFranchises: MultFranchisesToCustomDropDownOptions(multiFranchises),
      bondInsurers: InsurersToCustomDropDownOptions(true, bondInsurers || []),
      enableFireInsuranceMenu,
      activeEstateId,
      accessibleEstates,
      lenderInsuranceEnabled:
        user.role === UserRolesEnum.brokerAdmin ||
        user.role === UserRolesEnum.brokerAnalyst ||
        (user.role?.includes('ESTATE') && user.estate?.lenderInsuranceEnabled),
      lenderInsuranceRate: user.estate?.lenderInsuranceRate
      ,
      standaloneChargesEnabled:
        user.role === UserRolesEnum.brokerAdmin ||
        user.role === UserRolesEnum.brokerAnalyst ||
        (user.role?.includes('ESTATE') &&
          Boolean(
            estateOptions.find((estate: any) => estate.value?.id === activeEstateId)?.value
              ?.standaloneChargesEnabled || user.estate?.standaloneChargesEnabled
          ))
    });

    if (activeEstateId) {
      storage.set('active_estate_id', activeEstateId);
    }

    setIsLoading(false);
  }

  const location = useLocation();

  useEffect(() => {
    const updatePage = (menuItem: MenuItemProps | SubMenuItemProps) => {
      if (menuItem?.id !== currentPage?.id) {
        updateCurrentPage({
          id: menuItem.id,
          name: menuItem?.name,
          text: menuItem.text,
          title: menuItem?.title,
          parent: {
            id: currentPage?.parent?.id,
            name: currentPage?.parent?.name
          }
        });
      }
    };

    const currentPath = location?.pathname?.replace('/admin/', '');

    const menuSelected = menuOptions?.find(menu => menu?.route === currentPath);

    if (menuSelected) {
      updatePage(menuSelected);

      return;
    }

    for (const menu of menuOptions || []) {
      const itemSelected = menu?.submenuItems?.find(
        subMenuItem => subMenuItem?.route === currentPath
      );

      if (itemSelected) {
        updatePage(itemSelected);

        return;
      }
    }
  }, [currentPage, location?.pathname, updateCurrentPage]);

  useEffect(() => {
    getAvailableBrokersAndEstates();
  }, [user]);

  return (
    <>
      <Route path="/" />
      {!isLoading ? (
        <Route
          component={(props: MatchProps) => {
            return (
              <>
                <Sidebar {...componentProps}>
                  <Switch>
                    <Route path={`${props.match.url}/dashboard`} component={Dashboard} />
                    {menuOptions.find(menu => menu.name === 'FRANCHISES') && (
                      <Route
                        path={`${props.match.url}/franchises-dashboard`}
                        component={FranchisesDashboard}
                      />
                    )}

                    <Route
                      path={`${props.match.url}/budget`}
                      exact
                      component={FireInsuranceBudgetForm}
                    />

                    <Route
                      path={`${props.match.url}/fire-insurance-budget/:id?`}
                      component={FireInsuranceBudget}
                    />
                    <Route
                      path={`${props.match.url}/fire-insurance-budgets`}
                      exact
                      component={FireInsuranceBudgetList}
                    />
                    <Route
                      path={`${props.match.url}/fire-insurance-canceleds`}
                      exact
                      component={FireInsuranceCanceledList}
                    />
                    <Route path={`${props.match.url}/budgets`} component={Budgets} />
                    <Route
                      path={`${props.match.url}/fire-insurance-policies/:id?`}
                      component={FireInsurancePolicyList}
                    />
                    <Route
                      path={`${props.match.url}/fire-insurance-invoices`}
                      exact
                      component={FireFireInsuranceInvoices}
                    />

                    <Route
                      path={`${props.match.url}/fire-insurance-batches/:id`}
                      exact
                      component={FireInsuranceBatchDetails}
                    />
                    <Route
                      path={`${props.match.url}/fire-insurance-batches`}
                      exact
                      component={FireInsuranceBatchesDashboard}
                    />

                    <Route path={`${props.match.url}/estate`} component={EstateForm} />
                    <Route path={`${props.match.url}/estates`} component={Estates} />
                    <Route
                      path={`${props.match.url}/estate-units`}
                      exact
                      component={EstateUnitsListPage}
                    />
                    <Route
                      path={`${props.match.url}/estate-unit`}
                      exact
                      component={EstateUnitFormPage}
                    />
                    <Route
                      path={`${props.match.url}/estate-unit-edit/:id`}
                      exact
                      component={EstateUnitFormPage}
                    />
                    <Route
                      path={`${props.match.url}/estates-preregister/:id`}
                      component={estatePreregisterForm}
                    />
                    <Route
                      path={`${props.match.url}/estates-preregisters`}
                      component={EstatePreregisterListPage}
                    />
                    <Route path={`${props.match.url}/logs`} component={Events} />
                    <Route
                      path={`${props.match.url}/claim_estates`}
                      component={ClaimEstates}
                    />
                    <Route
                      path={`${props.match.url}/claim-estate-responsible-transfer`}
                      component={ClaimEstatesResponsibleTransfer}
                    />
                    <Route path={`${props.match.url}/proposals`} component={Proposals} />
                    <Route path={`${props.match.url}/policies`} component={Policies} />
                    <Route
                      path={`${props.match.url}/bond-insurance/:copy_id?`}
                      component={BondInsuranceForm}
                    />
                    <Route
                      path={`${props.match.url}/find-record`}
                      component={FindRecord}
                    />
                    <Route
                      path={`${props.match.url}/canceled-bond-insurances`}
                      component={CanceledBondInsurancesForm}
                    />
                    <Route
                      path={`${props.match.url}/expired-bond-insurances`}
                      component={ExpiredBondInsurancesForm}
                    />
                    <Route
                      path={`${props.match.url}/bond-insurances/:id?`}
                      component={BondInsurancesForm}
                    />
                    <Route
                      path={`${props.match.url}/bond-insurance-proposals`}
                      component={BondInsuranceProposalsForm}
                    />
                    <Route
                      path={`${props.match.url}/bond-insurance-policies`}
                      component={BondInsurancePoliciesForm}
                    />
                    <Route
                      path={`${props.match.url}/reports-form`}
                      component={ReportForm}
                    />
                    <Route path={`${props.match.url}/user`} component={UsersForm} />
                    <Route path={`${props.match.url}/users`} component={UsersList} />
                    <Route path={`${props.match.url}/users-edit`} component={UsersEdit} />
                    <Route
                      path={`${props.match.url}/franchises`}
                      component={FranchisesList}
                    />
                    <Route
                      path={`${props.match.url}/franchise/:id`}
                      component={FranchiseForm}
                    />
                    <Route
                      path={`${props.match.url}/master-franchises`}
                      component={masterFranchisesList}
                    />
                    <Route
                      path={`${props.match.url}/estate-edit/:id`}
                      component={UpdateEstateForm}
                    />
                    <Route
                      path={`${props.match.url}/change-estate-email/:id`}
                      component={ChangeEmailForm}
                    />
                    <Route
                      path={`${props.match.url}/capitalizations`}
                      component={CapitalizationIndex}
                    />
                    <Route
                      path={`${props.match.url}/lender-insurances`}
                      component={LenderInsuranceList}
                    />
                    <Route
                      path={`${props.match.url}/lender-insurance-batches`}
                      component={LenderInsuranceBatchList}
                    />
                    <Route path={`${props.match.url}/powerbi`} component={PowerBIPage} />
                    <Route
                      path={`${props.match.url}/operational-bi`}
                      component={OperationalBIPage}
                    />
                    <Route
                      path={`${props.match.url}/banner-management`}
                      component={BannerManagement}
                    />
                    <Route
                      path={`${props.match.url}/blacklist`}
                      component={BlacklistPage}
                    />

                    <Route path={`${props.match.url}/reports`} component={Reports} />
                    <Route
                      path={`${props.match.url}/notifications`}
                      component={Notifications}
                    />

                    <Route path={`${props.match.url}/financial`} component={Financial} />
                    <Route
                      path={`${props.match.url}/standalone-charges`}
                      component={StandaloneChargesPage}
                    />
                    <Route
                      path={`${props.match.url}/properties/import`}
                      component={PropertiesImportPage}
                    />
                    <Route
                      path={`${props.match.url}/properties`}
                      component={PropertiesWalletPage}
                    />

                    <Route
                      path={`${props.match.url}/integrations-dashboard`}
                      component={IntegrationsDashboard}
                    />

                    <Route component={PageNotFound} />
                  </Switch>
                </Sidebar>
              </>
            );
          }}
        />
      ) : (
        <Loader active={isLoading} />
      )}
    </>
  );
};

export default connect(mapState, mapDispatch)(Routes);
